import { useState, useEffect } from 'react';
import { getBranchen, getBranchenDefaults, getBrancheIcons } from '../../api/onboardingApi';
import { Loader2, Check } from '../../icons/wp-icons';

// Branchen-Emojis (Fallback wenn keine Custom-Icons vorhanden)
const BRANCHE_EMOJIS = {
  glaserei: '🪟',
  tischlerei: '🪑',
  schreinerei: '🪚',
  sanitaer: '🚿',
  elektro: '⚡',
  maler: '🖌️',
  dachdecker: '🏠',
  fliesenleger: '🧱',
  metallbau: '⚙️',
  zimmerei: '🪵',
  trockenbau: '🏗️',
  bodenleger: '🪵',
  gartenbau: '🌳',
  klimatechnik: '❄️',
  gebaeudereinigung: '🧹',
  kfz: '🚗',
  sonstiges: '🔧'
};

function BrancheStep({ data = {}, onDataChange, onboardingData }) {
  const [branchen, setBranchen] = useState([]);
  const [brancheIcons, setBrancheIcons] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedBranche, setSelectedBranche] = useState(data.branche || onboardingData?.branchenKonfiguration?.branche || '');
  const [brancheCustom, setBrancheCustom] = useState(data.brancheCustom || onboardingData?.branchenKonfiguration?.brancheCustom || '');
  const [inhaberAnrede, setInhaberAnrede] = useState(data.inhaberAnrede || onboardingData?.branchenKonfiguration?.inhaberAnrede || '');

  // Branchen und Icons laden
  useEffect(() => {
    const loadData = async () => {
      try {
        // Parallel laden für bessere Performance
        const [branchenList, iconsData] = await Promise.all([
          getBranchen(),
          getBrancheIcons().catch(() => ({ icons: {} })) // Fehler abfangen falls keine Icons
        ]);
        setBranchen(branchenList);
        setBrancheIcons(iconsData.icons || {});
      } catch (err) {
        console.error('Fehler beim Laden der Branchen:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Daten an Parent übergeben
  // onDataChange ist stabil (useCallback), daher nicht in deps
  useEffect(() => {
    const isValid = selectedBranche && (selectedBranche !== 'sonstiges' || brancheCustom.trim());
    onDataChange({
      branche: selectedBranche,
      brancheCustom: selectedBranche === 'sonstiges' ? brancheCustom : null,
      inhaberAnrede
    }, isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranche, brancheCustom, inhaberAnrede]);

  const handleBrancheSelect = async (branche) => {
    setSelectedBranche(branche);

    // Branchen-Defaults laden für Vorschau
    if (branche && branche !== 'sonstiges') {
      try {
        const defaults = await getBranchenDefaults(branche);
        console.log('Branchen-Defaults:', defaults);
      } catch (err) {
        console.error('Fehler beim Laden der Defaults:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#378ab2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Wählen Sie Ihre Branche
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Wir passen Ihren KI-Assistenten an Ihr Gewerk an, damit er Kundenanfragen passend beantwortet.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {branchen.map((branche) => {
            const brancheKey = branche.key || branche.code;
            return (
              <button
                key={brancheKey}
                onClick={() => handleBrancheSelect(brancheKey)}
                className={`
                  relative p-4 rounded-xl border-2 text-left transition-all
                  ${selectedBranche === brancheKey
                    ? 'border-[#378ab2] bg-[#378ab2]/5 ring-2 ring-[#378ab2]/20'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                `}
              >
                {selectedBranche === brancheKey && (
                  <div className="absolute top-2 right-2">
                    <div className="w-5 h-5 bg-[#378ab2] rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-center h-12 mb-2">
                  {brancheIcons[brancheKey]?.iconPath ? (
                    <img
                      src={brancheIcons[brancheKey].iconPath}
                      alt={branche.label}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <span className="text-3xl">{BRANCHE_EMOJIS[brancheKey] || '🔧'}</span>
                  )}
                </div>
                <div className="font-medium text-gray-900">
                  {branche.label}
                </div>
                {branche.produktBezeichnung && (
                  <div className="text-xs text-gray-500 mt-1">
                    z.B. {branche.produktBeispiele?.slice(0, 2).join(', ')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom-Branche Eingabe */}
      {selectedBranche === 'sonstiges' && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Beschreiben Sie Ihre Branche
          </label>
          <input
            type="text"
            value={brancheCustom}
            onChange={(e) => setBrancheCustom(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] focus:border-transparent"
            placeholder="z.B. Klimatechnik, Sonnenschutz..."
          />
        </div>
      )}

      {/* Inhaber-Anrede (optional) */}
      {selectedBranche && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Wie sollen Kunden Sie ansprechen? (optional)
          </label>
          <input
            type="text"
            value={inhaberAnrede}
            onChange={(e) => setInhaberAnrede(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] focus:border-transparent bg-white"
            placeholder="z.B. Herr Müller, Frau Schmidt..."
          />
          <p className="text-xs text-gray-500 mt-2">
            Diese Anrede wird vom KI-Assistenten in E-Mails verwendet.
          </p>
        </div>
      )}
    </div>
  );
}

export default BrancheStep;
